import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiCloseLine,
  RiEyeLine,
  RiEyeOffLine,
  RiRefreshLine,
} from 'react-icons/ri';

const WidgetManager = ({
  isOpen,
  onClose,
  preferences,
  widgets,
  onToggle,
  onMove,
  onReset,
}) => {
  const { t } = useTranslation();
  const panelRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose();
      }
    };
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-8 z-20 w-72 rounded-2xl border border-on-surface/15 bg-surface shadow-lg p-3"
      role="dialog"
      aria-label={t('rightSidebar.manager.title')}
    >
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-on-surface">
          {t('rightSidebar.manager.title')}
        </h4>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-full hover:bg-on-surface/10 text-on-surface/70"
          aria-label={t('rightSidebar.manager.close')}
        >
          <RiCloseLine />
        </button>
      </div>
      <p className="text-xs text-on-surface/60 mb-2">
        {t('rightSidebar.manager.hint')}
      </p>
      <ul className="flex flex-col gap-1">
        {preferences.map((pref, index) => {
          const widget = widgets[pref.id];
          if (!widget) return null;
          const isPinned = widget.pinToTop === true;
          const isFirst = index === 0;
          const isLast = index === preferences.length - 1;
          return (
            <li
              key={pref.id}
              className="flex items-center justify-between gap-2 rounded-lg px-2 py-1 hover:bg-on-surface/5"
            >
              <div className="flex flex-col min-w-0">
                <span className="text-sm text-on-surface truncate">
                  {t(widget.titleKey)}
                  {isPinned && (
                    <span className="ml-1 text-xs text-on-surface/40">
                      (fixo)
                    </span>
                  )}
                </span>
                <span className="text-xs text-on-surface/50">
                  {pref.enabled
                    ? t('rightSidebar.manager.statusVisible')
                    : t('rightSidebar.manager.statusHidden')}
                </span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => onMove(pref.id, -1)}
                  disabled={isFirst || isPinned}
                  className="p-1 rounded-full text-on-surface/70 hover:bg-on-surface/10 disabled:opacity-30 disabled:hover:bg-transparent"
                  aria-label={t('rightSidebar.manager.moveUp')}
                >
                  <RiArrowUpSLine />
                </button>
                <button
                  type="button"
                  onClick={() => onMove(pref.id, 1)}
                  disabled={isLast || isPinned}
                  className="p-1 rounded-full text-on-surface/70 hover:bg-on-surface/10 disabled:opacity-30 disabled:hover:bg-transparent"
                  aria-label={t('rightSidebar.manager.moveDown')}
                >
                  <RiArrowDownSLine />
                </button>
                <button
                  type="button"
                  onClick={() => onToggle(pref.id)}
                  className="p-1 rounded-full text-on-surface/70 hover:bg-on-surface/10"
                  aria-label={
                    pref.enabled
                      ? t('rightSidebar.manager.hide')
                      : t('rightSidebar.manager.show')
                  }
                >
                  {pref.enabled ? <RiEyeLine /> : <RiEyeOffLine />}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1 text-xs text-primary hover:underline"
        >
          <RiRefreshLine />
          {t('rightSidebar.manager.reset')}
        </button>
      </div>
    </div>
  );
};

WidgetManager.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  preferences: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      enabled: PropTypes.bool.isRequired,
    })
  ).isRequired,
  widgets: PropTypes.objectOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      titleKey: PropTypes.string.isRequired,
    })
  ).isRequired,
  onToggle: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

export default WidgetManager;
